import { Injectable } from '@angular/core';
import { ConvertHelper } from '@barthauer/abc/core';
import { LoginSettingsService, ProfileSettingsService } from '@barthauer/abc/core/authentication';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { CacheStorageAdapter, DataService } from '@barthauer/abc/core/data';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import { LocalSettingsService } from './local-settings.service';
import { ISettingInfo, IWritableSettings, SettingsCatalogueKey } from './models/settings-catalogue';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';

@Injectable()
export abstract class SettingsService<
  TSettings,
  TSettingsInternal extends TSettings & IWritableSettings<TSettings>,
  TSettingValue extends number | string = number | string,
  TSettingEnum extends Record<Extract<keyof TSettingEnum, string>, TSettingValue> = any
> extends LocalSettingsService<TSettings, TSettingsInternal, TSettingValue, TSettingEnum> {
  constructor(
    abcConfigService: AbcConfigService,
    private data: DataService,
    private loginSettingsService: LoginSettingsService,
    private profileSettingsService: ProfileSettingsService,
    private settingsCatalogueStorageKey: string = 'Settings',
    internalSettingsType: new () => TSettingsInternal,
    settingEnumType: TSettingEnum
  ) {
    super(abcConfigService, internalSettingsType, settingEnumType);
  }

  protected get settingsCatalogueStorageKeyName(): string {
    return this.settingsCatalogueStorageKey;
  }

  protected override buildSettingsCatalogueKey(): Observable<SettingsCatalogueKey> {
    return this.loginSettingsService.getCurrentLoginSettings().pipe(
      concatMap((loginSettings) =>
        this.profileSettingsService.getCurrentProfileSettings().pipe(
          map((profileSettings) =>
            loginSettings && profileSettings
              ? {
                  WebApi: this.abcConfigService.configuration.WebApi.BaseURL,
                  UserName: loginSettings.userName ?? ProfileSettingsService.ANONYMOUS_USER_NAME, // Value must not be empty!
                  ProfileId: profileSettings.id,
                }
              : null
          )
        )
      )
    );
  }

  public setCurrentSettings(settings: TSettings): Observable<void> {
    return this.writeSettingsToStorage(settings).pipe(
      concatMap((_) => {
        if (CacheStorageAdapter.forceOfflineMode) {
          return of(null);
        } else {
          return this.loginSettingsService.getCurrentLoginSettings().pipe(
            concatMap((s) => {
              if (s?.isAnonymousLogin || s?.access_token) {
                return this.data
                  .executeSetValueQuery(
                    new ParameterValue(this.settingsCatalogueStorageKeyName, settings ? JSON.stringify(settings) : null)
                  )
                  .pipe(
                    catchError((err) => {
                      if (CacheStorageAdapter.useOfflineMode) {
                        return of(null);
                      }
                      throw err;
                    }),
                    map((_) => {})
                  );
              } else {
                return of(null);
              }
            })
          );
        }
      })
    );
  }

  public removeCurrentSettings(): Observable<void> {
    return this.setCurrentSettings(null);
  }

  public getCurrentSettings(): Observable<TSettings> {
    return this.readSettingsFromStorage().pipe(
      concatMap((settings) => {
        if (settings) {
          return of(settings);
        } else if (CacheStorageAdapter.forceOfflineMode) {
          return of(null);
        } else {
          return this.loginSettingsService.getCurrentLoginSettings().pipe(
            concatMap((s) => {
              if (s?.isAnonymousLogin || s?.access_token) {
                return this.data.executeGetValueQuery(new ParameterValue(this.settingsCatalogueStorageKeyName)).pipe(
                  map((_) => {
                    try {
                      return ConvertHelper.fromString(this.internalSettingsType, _?.toString()) as TSettings;
                    } catch (err) {
                      console.log(err);
                      return null;
                    }
                  }),
                  catchError((err) => {
                    if (CacheStorageAdapter.useOfflineMode) {
                      return of(null);
                    }
                    throw err;
                  })
                );
              } else {
                return of(null);
              }
            })
          );
        }
      })
    );
  }

  public async setObjectSetting(key: TSettingEnum, name: string, value: any): Promise<void> {
    const settings =
      (await this.getCurrentSettings().toPromise()) ?? <TSettings>(<unknown>new this.internalSettingsType());
    const infos = this.settingInfos;
    const info: ISettingInfo = infos?.get(key);
    if (info) {
      let obj: Object = settings[info.settingName];
      if (!obj) {
        obj = {};
      }
      obj[name] = value;
      settings[info.settingName] = obj;
      await this.setCurrentSettings(settings).toPromise();
    }
  }

  public async getObjectSetting(key: TSettingEnum, name: string): Promise<unknown> {
    const settings = await this.getCurrentSettings().toPromise();
    const infos = this.settingInfos;
    const info: ISettingInfo = infos?.get(key);
    if (settings && info) {
      const obj: Object = settings[info.settingName];
      if (obj) {
        return obj[name];
      }
    }
    return null;
  }
}
